import React, { useState, useEffect } from 'react';
import QuickPost from './QuickPost';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { multiLanguageAPI } from '../services/apiEndpoints';
import { DATAFORSEO_LOCATIONS } from '../data/locations';
import {
    GlobeIcon,
    RocketIcon,
    CheckCircledIcon,
    PlusIcon,
    ReloadIcon,
    LightningBoltIcon,
    UpdateIcon,
} from '@radix-ui/react-icons';

function MultiLanguageTrends({ user }) {
    const [loading, setLoading]               = useState(false);
    const [error, setError]                   = useState(null);
    const [trends, setTrends]                 = useState(null);
    const [showQuickPost, setShowQuickPost]     = useState(false);
    const [prefilledTopic, setPrefilledTopic]   = useState('');
    const [prefilledLocation, setPrefilledLocation] = useState('0');
    const [recentHistory, setRecentHistory]   = useState([]);

    useEffect(() => {
        multiLanguageAPI.getRecent()
            .then(res => { if (res.data?.success) setRecentHistory(res.data.data || []); })
            .catch(() => {});
    }, []);

    const refreshRecentHistory = () => {
        multiLanguageAPI.getRecent()
            .then(res => { if (res.data?.success) setRecentHistory(res.data.data || []); })
            .catch(() => {});
    };

    const handleLoadSnapshot = async (id) => {
        setLoading(true);
        setError(null);
        try {
            const res = await multiLanguageAPI.getById(id);
            if (res.data?.success) setTrends(res.data);
        } catch {
            setError('Could not load that snapshot. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleDiscover = async (force = false) => {
        setLoading(true);
        setError(null);
        try {
            const res  = await multiLanguageAPI.discover(force);
            const data = res.data;
            if (!data.success) { setError(data.message || 'Discovery failed.'); return; }
            setTrends(data);
            refreshRecentHistory();
        } catch (err) {
            setError(err.response?.data?.message || 'Discovery failed. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const openQuickPost = (trend) => {
        const loc = DATAFORSEO_LOCATIONS[trend.countryCode];
        setPrefilledTopic(trend.translation);
        setPrefilledLocation(loc?.code ? String(loc.code) : '0');
        setShowQuickPost(true);
    };

    const trendList = trends?.trends ?? [];

    const topOpportunity = trendList.find(t => t.isLocal) ?? trendList[0] ?? null;

    const statusBadge = (status) => {
        if (status === 'breakout') return <Badge className="bg-red-500/20 text-red-400 border-red-500/30 text-xs">Breakout</Badge>;
        if (status === 'new')      return <Badge className="bg-orange-500/20 text-orange-400 border-orange-500/30 text-xs">New</Badge>;
        return                            <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30 text-xs">Rising</Badge>;
    };

    const TrendCard = ({ trend }) => (
        <div className={`p-4 rounded-lg border transition-all ${
            trend.isLocal
                ? 'bg-emerald-500/10 border-emerald-500/30 hover:border-emerald-500/50'
                : 'bg-zinc-800/30 border-zinc-700 hover:border-zinc-600'
        }`}>
            <div className="flex items-start justify-between mb-2 gap-2">
                <div className="flex-1 min-w-0">
                    {trend.original !== trend.translation && (
                        <div className="text-xs text-zinc-500 mb-0.5 truncate">{trend.original}</div>
                    )}
                    <div className="text-white font-medium text-sm">"{trend.translation}"</div>
                </div>
                <div className="flex flex-col items-end gap-1 shrink-0">
                    {statusBadge(trend.status)}
                    {trend.isLocal && (
                        <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 text-xs">
                            Opportunity
                        </Badge>
                    )}
                </div>
            </div>

            <div className="flex items-center gap-2 mb-3">
                <LightningBoltIcon className="w-3.5 h-3.5 text-orange-400" />
                <span className="text-orange-400 font-bold text-sm">
                    +{trend.growth >= 5000 ? 'Breakout' : trend.growth.toLocaleString() + '%'}
                </span>
                {trend.isLocal
                    ? <span className="text-emerald-400 text-xs">Not yet trending globally!</span>
                    : <span className="text-zinc-500 text-xs">Already trending globally</span>
                }
            </div>

            {trend.flag && (
                <div className="text-xs text-zinc-500 mb-2">{trend.flag} {trend.country}</div>
            )}

            <Button
                size="sm"
                className={`w-full gap-2 text-white ${trend.isLocal ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-zinc-700 hover:bg-zinc-600'}`}
                onClick={() => openQuickPost(trend)}
            >
                <PlusIcon className="w-3 h-3" />
                Write Article
            </Button>
        </div>
    );

    return (
        <>
        <div className="text-zinc-50">
                <main className="flex-1 min-h-0 p-6 overflow-y-auto custom-scrollbar">
                    <div className="max-w-6xl mx-auto space-y-6">

                        {/* Header */}
                        <div className="text-center space-y-3 max-w-xl mx-auto py-6">
                            <h1 className="text-3xl font-extrabold tracking-tight bg-gradient-to-r from-white via-zinc-200 to-zinc-400 bg-clip-text text-transparent">
                                🌍 Global Trend Discovery
                            </h1>
                            <p className="text-zinc-400 text-sm">
                                Find trending topics worldwide before they hit your market
                            </p>
                        </div>

                        {/* Discover Button */}
                        {!trends && !loading && (
                            <div className="flex justify-center">
                                <Button
                                    onClick={() => handleDiscover(false)}
                                    size="lg"
                                    className="bg-violet-600 hover:bg-violet-700 gap-2 text-white"
                                >
                                    <GlobeIcon className="w-4 h-4" />
                                    Discover Worldwide Trends
                                </Button>
                            </div>
                        )}

                        {/* Recent History Pills */}
                        {recentHistory.length > 0 && !loading && (
                            <div className="flex flex-wrap gap-2 items-center justify-center">
                                <span className="text-zinc-500 text-xs">Recent:</span>
                                {recentHistory.map((item) => (
                                    <button
                                        key={item.id}
                                        onClick={() => handleLoadSnapshot(item.id)}
                                        disabled={loading}
                                        className="px-3 py-1 rounded-full text-xs bg-zinc-800/60 border border-white/10 text-zinc-300 hover:bg-zinc-700/60 hover:text-white transition-all disabled:opacity-50"
                                    >
                                        {new Date(item.discovered_at).toLocaleString(undefined, {
                                            month: 'short', day: 'numeric',
                                            hour: 'numeric', minute: '2-digit',
                                        })}
                                    </button>
                                ))}
                            </div>
                        )}

                        {/* Error */}
                        {error && (
                            <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-xl text-red-300 text-sm text-center">
                                {error}
                                <button onClick={() => handleDiscover(true)} className="ml-3 underline text-red-400 hover:text-red-200">
                                    Retry
                                </button>
                            </div>
                        )}

                        {/* Loading */}
                        {loading && (
                            <div className="py-20 flex flex-col items-center justify-center space-y-6">
                                <div className="relative">
                                    <div className="w-16 h-16 border-4 border-white/5 rounded-full" />
                                    <div className="absolute top-0 left-0 w-16 h-16 border-4 border-t-violet-500 rounded-full animate-spin" />
                                </div>
                                <div className="text-center space-y-2">
                                    <h3 className="text-xl font-semibold text-white">Scanning Global Trends...</h3>
                                    <p className="text-zinc-500 text-sm italic">
                                        "The future is already here — it's just not evenly distributed."
                                    </p>
                                </div>
                            </div>
                        )}

                        {/* Feature Cards (empty state) */}
                        {!trends && !loading && (
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div className="group bg-zinc-900/40 backdrop-blur-xl border border-white/10 rounded-xl p-4 hover:border-white/20 transition-all hover:shadow-lg hover:shadow-violet-500/10">
                                    <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-violet-500/20 to-purple-500/20 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                                        <RocketIcon className="w-6 h-6 text-violet-400" />
                                    </div>
                                    <h3 className="text-lg font-semibold text-white mb-1.5">First-Mover Advantage</h3>
                                    <p className="text-zinc-400 text-xs">Discover trends before they hit your market and be the first to cover them.</p>
                                </div>
                                <div className="group bg-zinc-900/40 backdrop-blur-xl border border-white/10 rounded-xl p-4 hover:border-white/20 transition-all hover:shadow-lg hover:shadow-blue-500/10">
                                    <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-blue-500/20 to-cyan-500/20 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                                        <GlobeIcon className="w-6 h-6 text-blue-400" />
                                    </div>
                                    <h3 className="text-lg font-semibold text-white mb-1.5">Worldwide Coverage</h3>
                                    <p className="text-zinc-400 text-xs">One scan covers all global markets — fast, efficient, and up to date.</p>
                                </div>
                                <div className="group bg-zinc-900/40 backdrop-blur-xl border border-white/10 rounded-xl p-4 hover:border-white/20 transition-all hover:shadow-lg hover:shadow-emerald-500/10">
                                    <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-emerald-500/20 to-green-500/20 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                                        <CheckCircledIcon className="w-6 h-6 text-emerald-400" />
                                    </div>
                                    <h3 className="text-lg font-semibold text-white mb-1.5">Unique Content Ideas</h3>
                                    <p className="text-zinc-400 text-xs">Get fresh content ideas your competitors haven't thought of yet.</p>
                                </div>
                            </div>
                        )}

                        {/* Results */}
                        {trends && !loading && (
                            <div className="space-y-6">
                                {/* Cache badge + refresh */}
                                <div className="flex items-center justify-between gap-4 flex-wrap">
                                    <div className="flex items-center gap-3">
                                        {trends.is_fresh === false && (
                                            <Badge className="bg-zinc-700/50 text-zinc-400 border-zinc-600/50 text-xs gap-1">
                                                <UpdateIcon className="w-3 h-3" /> Cached
                                            </Badge>
                                        )}
                                        {trends.is_fresh && (
                                            <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 text-xs">Live data</Badge>
                                        )}
                                        <span className="text-zinc-500 text-xs">{trendList.length} trends found</span>
                                    </div>
                                    <Button variant="outline" onClick={() => handleDiscover(true)} disabled={loading} className="gap-2">
                                        <ReloadIcon className="w-4 h-4" />
                                        Refresh Trends
                                    </Button>
                                </div>

                                {/* Top Opportunity Banner */}
                                {topOpportunity && (
                                    <Card className="bg-gradient-to-br from-emerald-500/10 to-green-500/10 border-emerald-500/30">
                                        <CardHeader>
                                            <CardTitle className="text-lg text-white">💡 TOP OPPORTUNITY</CardTitle>
                                        </CardHeader>
                                        <CardContent className="space-y-3">
                                            <div className="p-4 bg-white/5 rounded-lg">
                                                <p className="text-white font-medium mb-1">
                                                    "{topOpportunity.translation}" — trending in {topOpportunity.flag} {topOpportunity.country}
                                                </p>
                                                {topOpportunity.original !== topOpportunity.translation && (
                                                    <p className="text-zinc-500 text-xs mb-2">Original: {topOpportunity.original}</p>
                                                )}
                                                <p className="text-emerald-400 text-sm mb-1">
                                                    Not yet trending globally! (+{topOpportunity.growth >= 5000 ? 'Breakout' : topOpportunity.growth.toLocaleString() + '%'} growth)
                                                </p>
                                                <p className="text-zinc-300 text-sm">Be the first to cover this topic!</p>
                                            </div>
                                            <Button
                                                className="w-full bg-emerald-600 hover:bg-emerald-700 gap-2 text-white"
                                                onClick={() => openQuickPost(topOpportunity)}
                                            >
                                                <PlusIcon className="w-4 h-4" />
                                                Write Article Now
                                            </Button>
                                        </CardContent>
                                    </Card>
                                )}

                                {/* Trend Grid */}
                                <Card className="bg-zinc-900/40 backdrop-blur-xl border-white/10">
                                    <CardHeader>
                                        <CardTitle className="text-lg text-white flex items-center gap-2">
                                            🔥 Worldwide Rising Trends
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            {trendList
                                                .filter(t => t !== topOpportunity)
                                                .map((trend, idx) => (
                                                    <TrendCard key={idx} trend={trend} />
                                                ))}
                                        </div>
                                    </CardContent>
                                </Card>
                            </div>
                        )}
                    </div>
                </main>
        </div>

        {showQuickPost && (
            <QuickPost
                user={user}
                onClose={() => setShowQuickPost(false)}
                prefilledTopic={prefilledTopic}
                prefilledLocation={prefilledLocation}
            />
        )}
        </>
    );
}

export default MultiLanguageTrends;
